import React, { useEffect, useRef, useState } from "react";
import { AnswerResult } from "../Quiz/answer.ts";
import { PickIndex } from "./chartGame.ts";
import { Direction, MULTIPLICATION_ONLY } from "./munchGame.ts";
import { explain, factFront, ruleText, squareText } from "./MunchGame.tsx";
import {
    COLS,
    CollieState,
    directionBetween,
    GOAL_ROW,
    indexOf,
    move,
    nextFloor,
    Position,
    respawn,
    ROWS,
    START_ROW,
    starsFor,
    startCollie,
    Tile,
} from "./collieGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface CollieGameProps {
    pick: PickIndex;
    /** What the player chose to practise; "Makes n" tiles are facts of these (ADR 0098 §4). */
    operations?: string[];
    /** A "Makes n" tile stepped on - the only kind that is a fact. `front` is the generator's own text, "7 x 8". */
    onFact(front: string, result: AnswerResult): void;
    /** A floor was crossed. */
    onCrossed(state: CollieState): void;
}

/** How long the fall plays before she is back on the start ledge. */
export const FALL_MS = 900;

const KEYS: Record<string, Direction> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    w: "up",
    s: "down",
    a: "left",
    d: "right",
};

/** The d-pad, in grid order. `labelKey` is a translation key, not display text. */
const PAD: { action: Direction; labelKey: StringKey; glyph: string }[] = [
    { action: "up", labelKey: "flashCards.munchUp", glyph: "↑" },
    { action: "left", labelKey: "flashCards.munchLeft", glyph: "←" },
    { action: "right", labelKey: "flashCards.munchRight", glyph: "→" },
    { action: "down", labelKey: "flashCards.munchDown", glyph: "↓" },
];

const COL_INDEXES = Array.from({ length: COLS }, (_, c) => c);
/** Far side at the top, start ledge at the bottom. */
const ROW_ORDER = [GOAL_ROW, ...Array.from({ length: ROWS }, (_, i) => ROWS - 1 - i), START_ROW];

/** A black-and-white border collie, drawn here so the art is the site's own (ADR 0057). */
export function BorderCollie({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
            <path d="M35 36 q11 -3 9 -15" stroke="#111827" strokeWidth="4" fill="none" strokeLinecap="round" />
            <path d="M43.6 23.5 q0.9 -1.8 0.3 -3" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" />
            <ellipse cx="24" cy="36" rx="12" ry="9" fill="#111827" />
            <ellipse cx="24" cy="38" rx="6" ry="7" fill="#ffffff" />
            <ellipse cx="17.5" cy="45" rx="3.6" ry="2.3" fill="#ffffff" />
            <ellipse cx="30.5" cy="45" rx="3.6" ry="2.3" fill="#ffffff" />
            <path d="M11 11 L15 1.5 L20 10 Z" fill="#111827" />
            <path d="M37 11 L33 1.5 L28 10 Z" fill="#111827" />
            <path d="M15 1.5 L13.5 6 L17 5.5 Z" fill="#374151" />
            <path d="M33 1.5 L34.5 6 L31 5.5 Z" fill="#374151" />
            <circle cx="24" cy="17" r="11" fill="#111827" />
            <path d="M22.3 6.2 h3.4 l2.4 12 h-8.2 z" fill="#ffffff" />
            <ellipse cx="24" cy="22" rx="6.6" ry="5.2" fill="#ffffff" />
            <circle cx="18.8" cy="15" r="1.9" fill="#78350f" />
            <circle cx="29.2" cy="15" r="1.9" fill="#78350f" />
            <circle cx="19.3" cy="14.4" r="0.6" fill="#ffffff" />
            <circle cx="29.7" cy="14.4" r="0.6" fill="#ffffff" />
            <ellipse cx="24" cy="19.8" rx="2.3" ry="1.6" fill="#111827" />
            <path d="M22.4 23.6 q1.6 3.4 3.2 0 z" fill="#f472b6" />
        </svg>
    );
}

/**
 * One run of Collie Crossing (ADR 0057). There is no clock: every change is a
 * key press, a tap, or the end of a fall, and the engine (`collieGame.ts`)
 * decides what each one does.
 */
export function CollieGame({ pick, operations = MULTIPLICATION_ONLY, onFact, onCrossed }: CollieGameProps) {
    const t = useTranslation();
    const [game, setGame] = useState<CollieState>(() => startCollie(pick, operations));
    const [message, setMessage] = useState<{ text: string; tone: "good" | "bad" } | null>(null);
    const boardRef = useRef<HTMLDivElement>(null);
    const handledSeq = useRef(0);

    useEffect(() => {
        boardRef.current?.focus();
    }, []);

    useEffect(() => {
        if (game.phase !== "falling") {
            return;
        }
        const timer = setTimeout(() => setGame((g) => respawn(g)), FALL_MS);
        return () => clearTimeout(timer);
    }, [game.phase]);

    // Each event is handled once, keyed on its sequence number.
    const event = game.lastEvent;
    useEffect(() => {
        if (!event || event.seq === handledSeq.current) {
            return;
        }
        handledSeq.current = event.seq;
        if (event.kind === "stepped") {
            setMessage({ text: format(t("flashCards.collieSafeTemplate"), { text: squareText(event.tile) }), tone: "good" });
            if (event.tile.a !== undefined) {
                onFact(factFront(event.tile), "correct");
            }
        } else if (event.kind === "fell") {
            const why = explain(game.rule, event.tile, t);
            setMessage({ text: format(t("flashCards.collieFellTemplate"), { why }), tone: "bad" });
            if (event.tile.a !== undefined) {
                onFact(factFront(event.tile), "wrong");
            }
        } else if (event.kind === "crossed") {
            setMessage(null);
            onCrossed(game);
        } else {
            setMessage(null);
        }
    }, [event, game, onFact, onCrossed, t]);

    function go(dir: Direction) {
        setGame((g) => move(g, dir));
    }

    function advance() {
        setGame((g) => nextFloor(g, pick));
        boardRef.current?.focus();
    }

    function onKeyDown(e: React.KeyboardEvent) {
        const dir = KEYS[e.key] ?? KEYS[e.key.toLowerCase()];
        if (dir) {
            e.preventDefault();
            go(dir);
        } else if (e.key === "Enter" && game.phase === "crossed") {
            e.preventDefault();
            advance();
        }
    }

    function tap(pos: Position) {
        boardRef.current?.focus();
        const dir = directionBetween(game.dog, pos);
        if (dir) {
            go(dir);
        }
    }

    const keepBoardFocus = (e: React.MouseEvent) => e.preventDefault();
    const dogHere = (pos: Position) => game.dog.row === pos.row && game.dog.col === pos.col;

    function tileLabel(tile: Tile, pos: Position): string {
        const parts = [squareText(tile)];
        if (tile.broken) parts.push(t("flashCards.collieTileBroken"));
        else if (tile.stepped) parts.push(t("flashCards.collieTileSafe"));
        if (dogHere(pos)) parts.push(t("flashCards.collieDogHere"));
        return parts.join(", ");
    }

    function dog(pos: Position) {
        if (!dogHere(pos)) {
            return null;
        }
        const state = game.phase === "falling" ? " collieDog--falling" : game.phase === "crossed" ? " collieDog--happy" : "";
        return <BorderCollie className={`collieDog${state}`} />;
    }

    function ledge(row: number) {
        const goal = row === GOAL_ROW;
        return (
            <div key={row} className={`collieLedge ${goal ? "collieLedge--goal" : "collieLedge--start"}`}>
                <span className="collieLedgeLabel">
                    {goal ? t("flashCards.collieOtherSide") : t("flashCards.collieStartLedge")}
                </span>
                {COL_INDEXES.map((col) => {
                    const pos = { row, col };
                    return (
                        <div
                            key={col}
                            className="collieLedgeCell"
                            aria-label={dogHere(pos) ? t("flashCards.collieDogHere") : undefined}
                            onClick={() => tap(pos)}
                        >
                            {dog(pos)}
                        </div>
                    );
                })}
            </div>
        );
    }

    const stars = starsFor(game.falls);

    return (
        <div className="collieGame">
            <div className="quizStatus collieStatus">
                <span className="dropLevel">{format(t("flashCards.collieFloorTemplate"), { n: game.floor })}</span>
                <span className="collieFalls">{format(t("flashCards.collieFallsTemplate"), { n: game.falls })}</span>
            </div>
            <p className="collieRule" aria-live="polite">
                {ruleText(game.rule, t)}
            </p>

            <div
                ref={boardRef}
                className="collieBoard"
                tabIndex={0}
                role="application"
                aria-label={t("flashCards.collieBoardLabel")}
                onKeyDown={onKeyDown}
            >
                {ROW_ORDER.map((row) =>
                    row === GOAL_ROW || row === START_ROW ? (
                        ledge(row)
                    ) : (
                        <div key={row} className="collieRow">
                            {COL_INDEXES.map((col) => {
                                const pos = { row, col };
                                const tile = game.tiles[indexOf(pos)];
                                const classes = [
                                    "collieTile",
                                    tile.broken ? "collieTile--broken" : "",
                                    tile.stepped ? "collieTile--stepped" : "",
                                    directionBetween(game.dog, pos) && !tile.broken ? "collieTile--near" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");
                                return (
                                    <div key={col} className={classes} aria-label={tileLabel(tile, pos)} onClick={() => tap(pos)}>
                                        <span className="collieTileValue">{squareText(tile)}</span>
                                        {tile.stepped && !tile.broken && (
                                            <span className="colliePaw" aria-hidden="true">
                                                🐾
                                            </span>
                                        )}
                                        {dog(pos)}
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            {game.phase === "crossed" ? (
                <div className="collieCrossed">
                    <p className="collieCrossedTitle">{t("flashCards.collieCrossed")}</p>
                    <p className="collieStars" role="img" aria-label={format(t("flashCards.collieStarsAriaTemplate"), { n: stars })}>
                        {"★".repeat(stars)}
                        <span className="collieStarsOff">{"★".repeat(3 - stars)}</span>
                    </p>
                    <p className="quizSummaryCounts">
                        {game.falls === 0
                            ? t("flashCards.collieCrossedClean")
                            : game.falls === 1
                            ? t("flashCards.collieCrossedOneFall")
                            : format(t("flashCards.collieCrossedFallsTemplate"), { n: game.falls })}
                    </p>
                    <button type="button" className="quizPlayAgain" onMouseDown={keepBoardFocus} onClick={advance}>
                        {t("flashCards.collieNextFloor")}
                    </button>
                </div>
            ) : (
                <p className={`munchMessage${message ? ` munchMessage--${message.tone}` : ""}`} aria-live="polite">
                    {message?.text}
                </p>
            )}

            <div className="munchPad colliePad">
                {PAD.map(({ action, labelKey: label, glyph }) => (
                    <button
                        key={action}
                        type="button"
                        className={`munchPadButton munchPad--${action}`}
                        aria-label={t(label)}
                        disabled={game.phase !== "walking"}
                        onMouseDown={keepBoardFocus}
                        onClick={() => go(action)}
                    >
                        {glyph}
                    </button>
                ))}
            </div>
        </div>
    );
}
